import React from "react";
import {
  Box,
  Card,
  CardHeader,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { keyframes, useTheme } from "@mui/material/styles";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { styles } from "../styles/TrustCenter.styles";
import FrameworkLogo from "./FrameworkLogo";
import RefreshButton from "./RefreshButton";
import { FrameworkStatusChip } from "./ComplianceSection";
import { companyLogoUrl } from "../../../apis/company-logo-apis/companyLogoApis";

const liveBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;

// Blinking green dot next to "Live data". Decorative: the text says it.
// Stays steady for visitors who ask their device for less motion.
const LiveDot = () => {
  const theme = useTheme();
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 8,
        height: 8,
        flexShrink: 0,
        borderRadius: "50%",
        backgroundColor: theme.customColors.status.success.main,
        animation: `${liveBlink} 1.4s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    />
  );
};

const CompanyLogo = ({ company }) => {
  const logoUrl = companyLogoUrl(company.logoPath);
  if (!logoUrl) return null;
  return (
    <Box
      component="img"
      src={logoUrl}
      alt={`${company.name} logo`}
      sx={{ height: 48, maxWidth: 160, objectFit: "contain" }}
    />
  );
};

// Vanta-style compliance tile: shield badge on top, name + status under it.
// One framework in the Overview's Compliance list (a column, like Vanta's).
const FrameworkRow = ({ framework }) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ px: 2, py: 1.5 }}
    >
      <FrameworkLogo framework={framework} size={36} alt="" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 14,
            color: theme.customColors.text.strong,
          }}
          noWrap
          title={framework.name}
        >
          {framework.name}
        </Typography>
      </Box>
      <FrameworkStatusChip status={framework.status} />
    </Stack>
  );
};

export const StatCard = ({ value, label, icon }) => {
  const theme = useTheme();
  return (
    <Box sx={styles.statsCard}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: theme.customColors.text.strong,
            }}
          >
            {value}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ color: theme.customColors.icons.primary }}>{icon}</Box>
      </Stack>
    </Box>
  );
};

const MetaLink = ({ href, children }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener"
    variant="body2"
    underline="hover"
  >
    {children}
  </Link>
);

// Company banner, shown above every tab (like Vanta's hero).
// `onRefresh` reloads the page data in place (the button next to Live data).
export const HeroSection = ({ data, onRefresh }) => {
  const theme = useTheme();
  const { company, updatedAt } = data;
  const links = [
    company.website && {
      key: "website",
      href: company.website,
      label: company.website.replace(/^https?:\/\//, ""),
    },
    company.privacyUrl && {
      key: "privacy",
      href: company.privacyUrl,
      label: "Privacy policy",
    },
    company.termsUrl && {
      key: "terms",
      href: company.termsUrl,
      label: "Terms of service",
    },
    ...(company.customLinks || []).map((link) => ({
      key: link.id,
      href: link.url,
      label: link.label,
    })),
  ].filter(Boolean);

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <CompanyLogo company={company} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: { xs: 22, sm: 26 },
            color: theme.customColors.text.strong,
          }}
        >
          Security at {company.name}
        </Typography>
      </Stack>
      <Typography
        sx={{
          mt: 1.5,
          color: theme.customColors.text.secondary,
          maxWidth: 760,
          lineHeight: 1.7,
          whiteSpace: "pre-line",
        }}
      >
        {company.overview}
      </Typography>

      <Stack
        useFlexGap
        direction="row"
        spacing={2.5}
        sx={{ mt: 2, flexWrap: "wrap", rowGap: 1, alignItems: "center" }}
      >
        {company.securityEmail && (
          <Link
            href={`mailto:${company.securityEmail}`}
            variant="body2"
            underline="hover"
          >
            {company.securityEmail}
          </Link>
        )}
        {links.map((link) => (
          <MetaLink key={link.key} href={link.href}>
            {link.label}
          </MetaLink>
        ))}
      </Stack>

      <Stack
        useFlexGap
        direction="row"
        spacing={2.5}
        sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}
        alignItems="center"
      >
        {company.dataResidency && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <PublicOutlinedIcon
              fontSize="small"
              sx={{ color: theme.customColors.icons.secondary }}
            />
            <Typography variant="body2">
              Data stored in <strong>{company.dataResidency}</strong>
            </Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={1} alignItems="center">
          <LiveDot />
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary }}
          >
            Live data · updated{" "}
            {new Date(updatedAt).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </Typography>
          {onRefresh && (
            <RefreshButton label="page" onRefresh={onRefresh} size={28} />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

// Three counts across the top of the Overview: what the company is
// certified against, how many controls it monitors, and how many vendors
// handle customer data.
// Always all three, 0 included: a missing card reads as a broken page,
// and "none" is itself an answer buyers look for.
export const OverviewStats = ({ data }) => {
  const stats = [
    {
      key: "frameworks",
      value: (data.frameworks || []).length,
      label: "Frameworks",
      icon: <ShieldOutlinedIcon />,
    },
    {
      key: "controls",
      value: (data.controls || []).length,
      label: "Controls",
      icon: <FactCheckOutlinedIcon />,
    },
    {
      key: "subprocessors",
      value: (data.subprocessors || []).length,
      label: "Subprocessors",
      icon: <BusinessOutlinedIcon />,
    },
  ];

  return (
    <Stack useFlexGap direction={{ xs: "column", sm: "row" }} spacing={2}>
      {stats.map(({ key, ...stat }) => (
        <StatCard key={key} {...stat} />
      ))}
    </Stack>
  );
};

// Overview tab: the Compliance list. Most companies show one or two
// frameworks, so it is a narrow column that is only as tall as its rows.
const OverviewSection = ({ data }) => {
  const theme = useTheme();
  const frameworks = data.frameworks || [];
  if (frameworks.length === 0) return null;

  return (
    <Card sx={{ ...styles.mainCard, overflow: "hidden" }}>
      <CardHeader sx={styles.cardHeader} title="Compliance" />
      <Stack
        divider={
          <Divider
            flexItem
            sx={{ borderColor: theme.customColors.border.main }}
          />
        }
      >
        {frameworks.map((framework) => (
          <FrameworkRow key={framework.id} framework={framework} />
        ))}
      </Stack>
    </Card>
  );
};

export default OverviewSection;
